import { useViewportLayout } from '@hooks/useViewportLayout'
import { MouseEvent, useCallback, useState } from 'react'

export type HoverZoomPreviewState = {
  url: string
  name: string
  x: number
  y: number
  zoomX: number
  zoomY: number
}

export const HOVER_ZOOM_PREVIEW_WIDTH = 246
export const HOVER_ZOOM_PREVIEW_HEIGHT = 242
const HOVER_ZOOM_PREVIEW_GAP = 16
const HOVER_ZOOM_PREVIEW_MARGIN = 8
const HOVER_ZOOM_PREVIEW_IMAGE_HEIGHT = 210
/** Menor que TexturesPanel (272%): preview más suave para productos del menú */
const HOVER_ZOOM_PREVIEW_SCALE = 155

export function computeHoverZoomPreview(
  event: MouseEvent<HTMLElement>,
  imageUrl: string,
  imageName: string,
): HoverZoomPreviewState {
  const rect = event.currentTarget.getBoundingClientRect()
  const relativeX = ((event.clientX - rect.left) / Math.max(1, rect.width)) * 100
  const relativeY = ((event.clientY - rect.top) / Math.max(1, rect.height)) * 100
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  const desiredXRight = event.clientX + HOVER_ZOOM_PREVIEW_GAP
  const desiredXLeft = event.clientX - HOVER_ZOOM_PREVIEW_WIDTH - HOVER_ZOOM_PREVIEW_GAP
  const x =
    desiredXRight + HOVER_ZOOM_PREVIEW_WIDTH + HOVER_ZOOM_PREVIEW_MARGIN <= viewportWidth
      ? desiredXRight
      : Math.max(HOVER_ZOOM_PREVIEW_MARGIN, desiredXLeft)

  const desiredY = event.clientY - HOVER_ZOOM_PREVIEW_GAP
  const y = Math.max(
    HOVER_ZOOM_PREVIEW_MARGIN,
    Math.min(desiredY, viewportHeight - HOVER_ZOOM_PREVIEW_HEIGHT - HOVER_ZOOM_PREVIEW_MARGIN),
  )

  return {
    url: encodeURI(imageUrl),
    name: imageName,
    x,
    y,
    zoomX: Math.max(0, Math.min(100, relativeX)),
    zoomY: Math.max(0, Math.min(100, relativeY)),
  }
}

// Corresponde a Atemporal commit 47ee2081: preview ampliado al hover — solo Desktop.
// En tablet/móvil no se montan handlers ni overlay (capa presentation).
export function useHoverZoomPreview() {
  const layout = useViewportLayout()
  const enabled = layout === 'desktop'
  const [hoverPreview, setHoverPreview] = useState<HoverZoomPreviewState | null>(null)

  const hideHoverPreview = useCallback(() => setHoverPreview(null), [])

  const showHoverPreview = useCallback(
    (event: MouseEvent<HTMLElement>, imageUrl: string, imageName: string) => {
      if (!enabled || !imageUrl) return
      setHoverPreview(computeHoverZoomPreview(event, imageUrl, imageName))
    },
    [enabled],
  )

  const getHoverPreviewHandlers = useCallback(
    (imageUrl: string, imageName: string) => {
      if (!enabled) return {}
      return {
        onMouseEnter: (event: MouseEvent<HTMLElement>) =>
          showHoverPreview(event, imageUrl, imageName),
        onMouseMove: (event: MouseEvent<HTMLElement>) =>
          showHoverPreview(event, imageUrl, imageName),
        onMouseLeave: hideHoverPreview,
      }
    },
    [enabled, showHoverPreview, hideHoverPreview],
  )

  return {
    hoverPreview: enabled ? hoverPreview : null,
    getHoverPreviewHandlers,
    hideHoverPreview,
  }
}

export function HoverZoomPreviewOverlay({ preview }: { preview: HoverZoomPreviewState | null }) {
  if (!preview) return null

  return (
    <div
      className='pointer-events-none fixed z-[120] rounded-md border border-C/Color2 bg-white p-2 shadow-2xl'
      style={{
        left: preview.x,
        top: preview.y,
        width: HOVER_ZOOM_PREVIEW_WIDTH,
      }}
    >
      <div
        className='h-[210px] w-full overflow-hidden rounded bg-white'
        style={{
          backgroundImage: `url("${preview.url}")`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: `${HOVER_ZOOM_PREVIEW_SCALE}%`,
          backgroundPosition: `${preview.zoomX}% ${preview.zoomY}%`,
        }}
      />
      <div className='mt-1 line-clamp-1 text-xs font-medium text-C/Color3'>{preview.name}</div>
    </div>
  )
}
